import { Node } from 'reactflow';
import { styled } from 'styled-components';

import useCSVDataViewer from './hooks/useCSVNodeViewer';
import { RightSideBarContent, RightSideBarHeader } from 'styles/commonStyles';
import NameFormRow from './components/NameFormRow';
import SeperatorFormRow from './components/SeperatorFormRow';
import FormRow from 'components/FormRow';
import Checkbox from 'components/ui/Checkbox';
import ColumnNameRow from './components/ColumnNameRow';
import ColumnTypeRow from './components/ColumnTypeRow';
import IdColumnRow from './components/IdColumnRow';
import DeleteColumnRow from './components/DeleteColumnRow';
import TargetColumnRow from './components/TargetColumnRow';
import NanColumnRow from './components/NanColumnRow';
import FillNanValueRow from './components/FillNanValueRow';
import EncodingRow from './components/EncodingRow';
import ScalerRow from './components/ScalerRow';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TableLayout = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ModeButtonView = styled.div`
  display: flex;
  gap: 10px;
`;

const ModeButton = styled.button`
  border: 1px solid ${(props) => props.theme.global.borderColor};
  padding: 6px 12px;
`;

const TableContainer = styled.div<{ $isDeleteMode: boolean }>`
  width: 100%;
  overflow: auto;
  padding-top: ${(props) => (props.$isDeleteMode ? '40px' : '0')};
`;

const Table = styled.table`
  th {
    position: relative;
    font-size: 14px;
    font-weight: 600;
    min-width: 84px;
    padding: 4px 10px;
    text-align: center;
    border: 1px solid ${(props) => props.theme.editor.csvTable.borderColor};
    background-color: ${(props) => props.theme.editor.csvTable.headBgColor};
    vertical-align: middle;
    white-space: nowrap;

    &:first-child {
      min-width: 74px;
    }
  }

  td {
    position: relative;
    font-size: 13px;
    border: 1px solid ${(props) => props.theme.editor.csvTable.borderColor};
    min-width: 84px;
    padding: 6px 10px;
    vertical-align: middle;

    div {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
`;

const DeleteCheckbox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: translateY(-100%);
  height: 40px;
`;

interface CSVNodeViewerProps {
  node: Node;
}

const CSVNodeViewer = ({ node }: CSVNodeViewerProps) => {
  const {
    columnLabels,
    useFormReturn,
    isEditMode,
    isDeleteMode,
    nodeDataSave,
    onAddColumnClick,
    onCancelClick,
    onColumnCheck,
    onConfirmClick,
    onDeleteModeClick,
    onEditModeClick,
  } = useCSVDataViewer({ id: node.id, blockData: node.data });
  const { watch } = useFormReturn;

  const columnNames = watch('columnNames');

  return (
    <RightSideBarContent>
      <RightSideBarHeader>
        <h1>CSV Data</h1>
      </RightSideBarHeader>
      <Container>
        <NameFormRow useFormReturn={useFormReturn} nodeDataSave={nodeDataSave} />
        <SeperatorFormRow useFormReturn={useFormReturn} nodeDataSave={nodeDataSave} />
        <FormRow label="데이터 테이블" direction="column">
          <TableLayout>
            <ModeButtonView>
              {isEditMode || isDeleteMode ? (
                <>
                  <ModeButton onClick={onConfirmClick}>확인</ModeButton>
                  <ModeButton onClick={onCancelClick}>취소</ModeButton>
                </>
              ) : (
                <>
                  <ModeButton onClick={onEditModeClick}>데이터 전처리</ModeButton>
                  <ModeButton onClick={onAddColumnClick}>컬럼 추가</ModeButton>
                  <ModeButton onClick={onDeleteModeClick}>컬럼 삭제</ModeButton>
                </>
              )}
            </ModeButtonView>
            <TableContainer $isDeleteMode={isDeleteMode}>
              <Table>
                <thead>
                  <tr>
                    <th></th>
                    {isEditMode && <th>전체</th>}
                    {columnLabels.map((col, i) => (
                      <th key={`column/label/${col}`}>
                        {isDeleteMode && i < columnNames.length && (
                          <DeleteCheckbox>
                            <Checkbox id={`delete/col/${i}`} onChange={(event) => onColumnCheck(event, i)} />
                          </DeleteCheckbox>
                        )}
                        <span>{col}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <ColumnNameRow
                    rowIndex={1}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                  <ColumnTypeRow
                    rowIndex={2}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                  <IdColumnRow
                    rowIndex={3}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                  <DeleteColumnRow
                    rowIndex={4}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                  <TargetColumnRow
                    rowIndex={5}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                  <NanColumnRow
                    rowIndex={6}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                  <FillNanValueRow
                    rowIndex={7}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                  <EncodingRow
                    rowIndex={8}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                  <ScalerRow
                    rowIndex={9}
                    columnLabels={columnLabels}
                    values={columnNames}
                    isEditMode={isEditMode}
                    useFormReturn={useFormReturn}
                  />
                </tbody>
              </Table>
            </TableContainer>
          </TableLayout>
        </FormRow>
      </Container>
    </RightSideBarContent>
  );
};

export default CSVNodeViewer;
